import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Badge } from 'primereact/badge';

import { defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, SkeletonbodyTemplate, Skeletonitems } from '../../appconfig/Settings';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails } from '../../service/ProductService';
import { GET_INVENTORY_LIST } from '../../config/ApiConstant';

export const InventoryList = () => {
    document.title = 'Inventory List | Karan Enterprise';

    const location: any = useLocation();
    const productData = location.state?.productData || {};

    const toast = useRef<any>(null);

    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [inventoryList, setInventoryList] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any>([]);

    useEffect(() => {
        getInventoryList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getInventoryList = async () => {
        try {
            setPageLoad(false);
            const queryParams = productData?.product_id ? `?product_id=${productData.product_id}` : '';
            const response = await getAllDetails(GET_INVENTORY_LIST + queryParams);
            if (response) {
                setInventoryList((response?.data || []).map((item: any, index: number) => ({ ...item, _rowId: `${item.product_id}_${index}` })));
                setPageLoad(true);
            }
        } catch (error) {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
            </>
        );
    };

    const productNameTemplate = (rowData: any) => {
        return <span className="font-bold">{rowData?.product_name}</span>;
    };

    const statusTemplate = (rowData: any) => {
        const severity = rowData?.status === 'approved' ? 'success' : rowData?.status === 'rejected' ? 'danger' : 'warning';
        return <Badge severity={severity} value={rowData?.status?.toUpperCase()} />;
    };

    const owrTemplate = (rowData: any) => {
        const isOwr = rowData?.is_owr === 1;
        return <Badge severity={isOwr ? 'success' : 'danger'} value={isOwr ? 'Yes' : 'No'} />;
    };

    const withoutGlaseTemplate = (rowData: any) => {
        const isWithoutGlase = rowData?.without_glase === 1;
        return <Badge severity={isWithoutGlase ? 'success' : 'danger'} value={isWithoutGlase ? 'Yes' : 'No'} />;
    };

    const rollsTemplate = (rowData: any) => {
        return (
            <span className="text-primary cursor-pointer">
                {rowData?.rolls?.length || 0} <i className="pi pi-eye ml-1" />
            </span>
        );
    };

    const rowExpansionTemplate = (data: any) => {
        const getRollStatusSeverity = (status: string) => {
            switch (status) {
                case 'in_stock':
                    return 'success';
                case 'in_production':
                    return 'info';
                case 'dispatched':
                    return 'info';
                case 'damaged':
                    return 'danger';
                case 'returned':
                    return 'warning';
                default:
                    return 'warning';
            }
        };

        return (
            <div className="p-3 surface-50 border-round">
                <h5 className="mb-3">Rolls for {data?.product_name}</h5>
                {data?.rolls && data?.rolls.length > 0 ? (
                    <DataTable value={data.rolls} stripedRows>
                        <Column field="roll_id" header="Roll ID" sortable />
                        <Column field="length" header="Length" sortable />
                        <Column field="status" header="Status" body={(rowData: any) => <Badge severity={getRollStatusSeverity(rowData.status)} value={rowData.status?.replace('_', ' ').toUpperCase()} />} sortable />
                    </DataTable>
                ) : (
                    <p className="text-gray-500">No rolls found</p>
                )}
            </div>
        );
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Inventory'} pageName={'Inventory List'} pageTitle={productData?.product_name || 'Inventory List'} />
            <div className="grid crud-demo expanded-table">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />
                        <Toolbar left={leftToolbarTemplate} className="mb-3"></Toolbar>
                        {pageLoad ? (
                            <>
                                <DataTable
                                    className="datatable-responsive"
                                    value={inventoryList}
                                    paginator={inventoryList.length >= 10 && true}
                                    globalFilter={globalFilter}
                                    rows={defaultRowOptions}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Inventory Data Found"
                                    showGridlines
                                    stripedRows
                                    expandedRows={expandedRows}
                                    onRowToggle={(e) => setExpandedRows(e.data)}
                                    rowExpansionTemplate={rowExpansionTemplate}
                                    dataKey="_rowId"
                                >
                                    <Column expander style={{ width: '3rem' }} />
                                    <Column field="product_name" header="Product Name" sortable filter body={productNameTemplate} />
                                    <Column field="micron_gsm" header="Micron/GSM" sortable />
                                    <Column field="width" header="Width" sortable />
                                    <Column field="is_owr" header="OWR" body={owrTemplate} />
                                    <Column field="without_glase" header="Without Glase" body={withoutGlaseTemplate} sortable filter />
                                    <Column field="total_length" header="Length" sortable />
                                    <Column field="count" header="Total Rolls" />
                                </DataTable>
                            </>
                        ) : (
                            <>
                                <DataTable value={Skeletonitems}>
                                    <Column field="product_name" header="Product Name" body={SkeletonbodyTemplate} />
                                    <Column field="width" header="Width" body={SkeletonbodyTemplate} />
                                    <Column field="length" header="Length" body={SkeletonbodyTemplate} />
                                    <Column field="micron_gsm" header="Micron/GSM" body={SkeletonbodyTemplate} />
                                    <Column field="is_owr" header="OWR" body={SkeletonbodyTemplate} />
                                    <Column field="without_glase" header="Without Glase" body={SkeletonbodyTemplate} />
                                    <Column field="count" header="Total Rolls" body={SkeletonbodyTemplate} />
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
